/**
 * Client-side chroma keying for the LiveAvatar video stream.
 *
 * LiveAvatar's session API has no background parameter — the curated studio
 * avatars stream on a solid green background, and the custom scene is
 * composited in the browser: every frame is drawn to a canvas, green pixels
 * (by HSV thresholds) get their alpha zeroed, and the background image behind
 * the canvas shows through. Same approach as HeyGen's reference demo
 * (https://docs.liveavatar.com/docs/guides/change-background).
 */

type ChromaKeyOptions = {
  /** Lower bound of the hue range (degrees) treated as green. */
  minHue: number;
  /** Upper bound of the hue range (degrees) treated as green. */
  maxHue: number;
  /** Minimum saturation for a pixel to qualify — keeps grays/whites intact. */
  minSaturation: number;
  /** How strongly green must dominate red/blue; raise if avatar edges vanish. */
  threshold: number;
};

const DEFAULT_CHROMA_KEY_OPTIONS: ChromaKeyOptions = {
  minHue: 60,
  maxHue: 180,
  minSaturation: 0.1,
  threshold: 1.0,
};

const MIN_PIXEL_BRIGHTNESS = 0.15;
const FULLY_TRANSPARENT_ALPHA_CUTOFF = 0.2;

/**
 * Draws the current video frame onto the canvas with green pixels keyed out.
 * No-ops until the video has decoded its first frame.
 */
const applyChromaKey = (
  sourceVideo: HTMLVideoElement,
  targetCanvas: HTMLCanvasElement,
  options: ChromaKeyOptions,
): void => {
  const ctx = targetCanvas.getContext('2d', { willReadFrequently: true, alpha: true });
  if (!ctx || sourceVideo.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;

  // Process at display size, not the video's native resolution (e.g. 480×270
  // vs 1920×1080) — drawImage scales the frame down before the pixel loop.
  targetCanvas.width = targetCanvas.clientWidth || sourceVideo.videoWidth;
  targetCanvas.height = targetCanvas.clientHeight || sourceVideo.videoHeight;

  ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
  ctx.drawImage(sourceVideo, 0, 0, targetCanvas.width, targetCanvas.height);

  const imageData = ctx.getImageData(0, 0, targetCanvas.width, targetCanvas.height);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let hue = 0;
    if (delta !== 0) {
      if (max === r) {
        hue = ((g - b) / delta) % 6;
      } else if (max === g) {
        hue = (b - r) / delta + 2;
      } else {
        hue = (r - g) / delta + 4;
      }
    }
    hue = Math.round(hue * 60);
    if (hue < 0) hue += 360;

    const saturation = max === 0 ? 0 : delta / max;
    const brightness = max / 255;

    const isGreen = hue >= options.minHue
      && hue <= options.maxHue
      && saturation > options.minSaturation
      && brightness > MIN_PIXEL_BRIGHTNESS
      && g > r * options.threshold
      && g > b * options.threshold;

    if (isGreen) {
      // Feather the edge: mostly-green pixels go fully transparent, pixels on
      // the avatar's outline keep partial alpha so hair/shoulders stay smooth.
      const greenness = (g - Math.max(r, b)) / (g || 1);
      const alpha = Math.max(0, 1 - greenness * 4);
      data[i + 3] = alpha < FULLY_TRANSPARENT_ALPHA_CUTOFF ? 0 : Math.round(alpha * 255);
    }
  }

  ctx.putImageData(imageData, 0, 0);
};

/**
 * Starts a requestAnimationFrame loop keying `sourceVideo` onto
 * `targetCanvas`. Returns a cleanup function that stops the loop.
 */
const TARGET_FRAME_INTERVAL_MS = 33; // ~30fps

const setupChromaKey = (
  sourceVideo: HTMLVideoElement,
  targetCanvas: HTMLCanvasElement,
  options: ChromaKeyOptions = DEFAULT_CHROMA_KEY_OPTIONS,
) => {
  let animationFrameId: number | null = null;
  let lastFrameTime = 0;

  const render = (now: number) => {
    animationFrameId = requestAnimationFrame(render);
    if (now - lastFrameTime < TARGET_FRAME_INTERVAL_MS) return;
    lastFrameTime = now;
    applyChromaKey(sourceVideo, targetCanvas, options);
  };

  animationFrameId = requestAnimationFrame(render);

  return () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
  };
};

export default setupChromaKey;
